import { ShopItem, Redemption } from '../types';

const BACKEND_API = 'https://forryshopapi.olevo.hu';
const WS_URL = 'wss://forryshopapi.olevo.hu';

export interface LeaderboardEntry {
    name: string;
    points: number;
    source: 'botrix' | 'local' | 'both';
    rank?: number;
}

export interface LeaderboardResponse {
    data: LeaderboardEntry[];
    page: number;
    pageSize: number;
    totalEntries: number;
    totalPages: number;
    searchTerm?: string;
}

export async function fetchShopItems(): Promise<ShopItem[]> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    try {
        const response = await fetch(`${BACKEND_API}/api/items`, {
            signal: controller.signal
        });
        clearTimeout(timeoutId);
        
        if (!response.ok) {
            throw new Error('Failed to fetch shop items');
        }
        return await response.json();
    } catch (error) {
        clearTimeout(timeoutId);
        if ((error as Error).name === 'AbortError') {
            console.error('Request timeout: Server did not respond within 3 seconds');
            throw new Error('Server timeout');
        }
        console.error('Error fetching shop items:', error);
        throw error;
    }
}

export async function fetchRedemptions(): Promise<Redemption[]> {
    try {
        const response = await fetch(`${BACKEND_API}/api/redemptions`);
        if (!response.ok) {
            throw new Error('Failed to fetch redemptions');
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching redemptions:', error);
        return [];
    }
}

export async function fetchChatStats(): Promise<number> {
    try {
        const response = await fetch(`${BACKEND_API}/api/chat-stats`);
        if (!response.ok) {
            throw new Error('Failed to fetch chat stats');
        }
        const data = await response.json();
        return data.chatMessages || 0;
    } catch (error) {
        console.error('Error fetching chat stats:', error);
        return 0;
    }
}

export async function fetchLeaderboard(page: number = 1): Promise<LeaderboardResponse> {
    try {
        const response = await fetch(`${BACKEND_API}/api/leaderboard?page=${page}`);
        if (!response.ok) {
            throw new Error('Failed to fetch leaderboard');
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching leaderboard:', error);
        return { data: [], page: 1, pageSize: 50, totalEntries: 0, totalPages: 0 };
    }
}

export async function searchLeaderboard(searchTerm: string, page: number = 1): Promise<LeaderboardResponse> {
    try {
        const response = await fetch(`${BACKEND_API}/api/leaderboard/search?q=${encodeURIComponent(searchTerm)}&page=${page}`);
        if (!response.ok) {
            throw new Error('Failed to search leaderboard');
        }
        return await response.json();
    } catch (error) {
        console.error('Error searching leaderboard:', error);
        return { data: [], page: 1, pageSize: 50, totalEntries: 0, totalPages: 0 };
    }
}

export function createWebSocket(
    onMessage: (redemption: Redemption) => void,
    onChatStats?: (count: number) => void,
    onOpen?: () => void,
    onClose?: () => void
): WebSocket {
    const ws = new WebSocket(WS_URL);
    let reconnectTimeout: NodeJS.Timeout | null = null;

    ws.onopen = () => {
        // console.log('[WS] Connected');
        onOpen?.();
    };

    ws.onmessage = (event) => {
        try {
            const message = JSON.parse(event.data);
            if (message.type === 'new_redemption') {
                onMessage(message.data);
            } else if (message.type === 'chat_stats' && onChatStats) {
                onChatStats(message.data.chatMessages);
            }
        } catch (error) {
            console.error('[WS] Error parsing message:', error);
        }
    };

    ws.onclose = () => {
        console.log('[WS] Disconnected - reconnecting in 3 seconds...');
        onClose?.();
        
        // Reconnect after 3 seconds
        reconnectTimeout = setTimeout(() => {
            console.log('[WS] Attempting to reconnect...');
            createWebSocket(onMessage, onChatStats, onOpen, onClose);
        }, 3000);
    };

    ws.onerror = (error) => {
        console.error('[WS] Error:', error);
    };

    // Store cleanup function on the WebSocket object
    (ws as any).cleanup = () => {
        if (reconnectTimeout) {
            clearTimeout(reconnectTimeout);
        }
    };

    return ws;
}
